"use client";

import { useState } from "react";
import Image from "next/image";
import SmoothImage from "@/utils/smoothImage";
import SectionTitle from "@/components/SectionTitle";

interface TabItem {
  id: string;
  title: string;
  subtitle: string;
  icon: string | React.ReactNode;
  images: string[];
  imageTitles?: string[];
}

interface AmenitiesTabsProps {
  tabsList?: TabItem[];
  title?: string;
  subTitle?: string;
  onImageClick?: (tabIndex: number, imageIndex: number) => void;
}

export default function AmenitiesTabs({
  tabsList,
  title,
  subTitle,
  onImageClick,
}: AmenitiesTabsProps) {
  const finalTabs = tabsList && tabsList.length > 0 ? tabsList : [];
  const [activeTab, setActiveTab] = useState<string>(finalTabs[0]?.id || "");

  const activeTabIndex = Math.max(
    0,
    finalTabs.findIndex((t) => t.id === activeTab),
  );

  // Find active tab item
  const currentTab = finalTabs[activeTabIndex] || finalTabs[0];

  if (!currentTab) return null;

  return (
    <section className="relative w-full bg-transparent py-12 md:py-16 border-t border-black/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        {/* Header Title */}
        <SectionTitle
          title={title}
          subtitle={subTitle}
          theme="green"
          subtitleColor="primaryHover"
          underlineColor="primaryHover"
          className="mb-8"
        />

        {/* TAB CONTROLS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-12">
          {finalTabs.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-3.5 p-4 rounded-xl border text-left transition-all duration-300 font-semibold text-xs md:text-sm ${
                  isActive
                    ? "bg-primaryHover text-white border-primaryHover shadow-lg shadow-primaryHover/20 scale-102"
                    : "bg-white text-black/80 border-black/5 hover:border-primaryHover/20 hover:shadow-md"
                }`}
              >
                <div
                  className={`p-2.5 rounded-lg flex items-center justify-center ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "bg-black/5 text-primaryHover"
                  }`}
                >
                  {typeof tab.icon === "string" ? (
                    <img
                      src={tab.icon}
                      alt={tab.title}
                      className="w-5 h-5 object-contain"
                    />
                  ) : (
                    tab.icon
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="font-extrabold uppercase tracking-wide">
                    {tab.title}
                  </span>
                  <span
                    className={`text-[10px] ${
                      isActive ? "text-white/60" : "text-black/45"
                    } font-bold`}
                  >
                    {tab.subtitle}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* GALLERY COLLAGE */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
          {/* Left Column: Landscape top + 2 Square bottom */}
          <div className="lg:col-span-8 flex flex-col gap-4">
            {/* Top image */}
            <div
              onClick={() => onImageClick?.(activeTabIndex, 0)}
              className="relative w-full aspect-[2.1/1] rounded-2xl overflow-hidden shadow-md border border-black/5 bg-gray-100 group cursor-pointer"
            >
              <SmoothImage
                src={currentTab.images[0] || "/images/hero_skyline.png"}
                alt={currentTab.imageTitles?.[0] || `${currentTab.title} 1`}
                fill
                sizes="(min-width: 1024px) 60vw, 90vw"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent transition-all duration-300" />
              <div className="absolute bottom-4 left-4 right-4">
                <h4 className="text-sm font-extrabold text-white uppercase tracking-wide mb-1">
                  {currentTab.imageTitles?.[0] || currentTab.title}
                </h4>
                <span className="text-[10px] font-black text-third uppercase tracking-wider flex items-center gap-1 group-hover:opacity-85">
                  Chi tiết tiện ích
                  <span>→</span>
                </span>
              </div>
            </div>

            {/* Bottom 2 images */}
            <div className="grid grid-cols-2 gap-4">
              <div
                onClick={() => onImageClick?.(activeTabIndex, 1)}
                className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-md border border-black/5 bg-gray-100 group cursor-pointer"
              >
                <SmoothImage
                  src={currentTab.images[1] || "/images/hero_skyline.png"}
                  alt={currentTab.imageTitles?.[1] || `${currentTab.title} 2`}
                  fill
                  sizes="auto"
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent transition-all duration-300" />
                <div className="absolute bottom-3 left-3 right-3">
                  <h4 className="text-xs sm:text-sm font-extrabold text-white uppercase tracking-wide mb-1 line-clamp-1">
                    {currentTab.imageTitles?.[1] || currentTab.title}
                  </h4>
                  <span className="text-[10px] font-black text-third uppercase tracking-wider flex items-center gap-1 group-hover:opacity-85">
                    Chi tiết tiện ích
                    <span>→</span>
                  </span>
                </div>
              </div>

              <div
                onClick={() => onImageClick?.(activeTabIndex, 2)}
                className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-md border border-black/5 bg-gray-100 group cursor-pointer"
              >
                <SmoothImage
                  src={currentTab.images[2] || "/images/hero_skyline.png"}
                  alt={currentTab.imageTitles?.[2] || `${currentTab.title} 3`}
                  fill
                  sizes="auto"
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent transition-all duration-300" />
                <div className="absolute bottom-3 left-3 right-3">
                  <h4 className="text-xs sm:text-sm font-extrabold text-white uppercase tracking-wide mb-1 line-clamp-1">
                    {currentTab.imageTitles?.[2] || currentTab.title}
                  </h4>
                  <span className="text-[10px] font-black text-third uppercase tracking-wider flex items-center gap-1 group-hover:opacity-85">
                    Chi tiết tiện ích
                    <span>→</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Tall portrait image */}
          <div className="lg:col-span-4">
            <div
              onClick={() => onImageClick?.(activeTabIndex, 3)}
              className="relative w-full h-full aspect-[2.1/1] lg:aspect-auto rounded-2xl overflow-hidden shadow-md border border-black/5 bg-gray-100 group cursor-pointer min-h-[220px]"
            >
              <SmoothImage
                src={currentTab.images[3] || "/images/hero_skyline.png"}
                alt={currentTab.imageTitles?.[3] || `${currentTab.title} 4`}
                fill
                sizes="auto"
                className="object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent transition-all duration-300" />
              <div className="absolute bottom-4 left-4 right-4">
                <h4 className="text-sm font-extrabold text-white uppercase tracking-wide mb-1">
                  {currentTab.imageTitles?.[3] || currentTab.title}
                </h4>
                <span className="text-[10px] font-black text-third uppercase tracking-wider flex items-center gap-1 group-hover:opacity-85">
                  Chi tiết tiện ích
                  <span>→</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
